
import React, { useState } from "react";

const LegalEntityTable = ({ legalEntities, loading, onEdit, onDelete }) => {
  const [selectedEntity, setSelectedEntity] = useState(null);

  const formatDate = (date) => {
    if (!date) return "-";
    const parts = date.split("-");
    if (parts.length !== 3) return date;
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  };

  const formatDateTime = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("en-GB");
  };

  const getStatusClass = (status) => {
    if (status === "ACTIVE") return "active";
    if (status === "PENDING") return "pending";
    if (status === "REJECTED") return "inactive";
    return "inactive";
  };

  if (loading) {
    return <div className="organization-loading">Loading legal entities...</div>;
  }

  if (!legalEntities || legalEntities.length === 0) {
    return (
      <div className="organization-empty">
        <div className="organization-empty-icon">+</div>
        <h3>No Legal Entities Found</h3>
        <p>There are no legal entities available for the current tenant.</p>
      </div>
    );
  }

  return (
    <>
      <div className="organization-table-wrapper">
        <table className="organization-table">
          <thead>
            <tr>
              <th>Registration Number</th>
              <th>Country</th>
              <th>Incorporation Date</th>
              <th>Tax Registrations</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {legalEntities.map((entity) => (
              <tr key={entity.id}>
                <td>
                  <div className="organization-name-cell">
                    <div className="organization-avatar">
                      {entity.registration_number?.charAt(0)?.toUpperCase() || "L"}
                    </div>

                    <div>
                      <strong>{entity.registration_number || "-"}</strong>
                      <span>Legal Entity</span>
                    </div>
                  </div>
                </td>

                <td>
                  <span className="organization-type-pill">
                    {entity.country_of_incorporation || "-"}
                  </span>
                </td>

                <td>{formatDate(entity.incorporation_date)}</td>

                <td>
                  <span className="organization-type-pill">
                    {Array.isArray(entity.tax_registrations) ? entity.tax_registrations.length : 0}
                  </span>
                </td>

                <td>
                  <span className={`organization-status ${getStatusClass(entity.status)}`}>
                    {entity.status || "Pending"}
                  </span>
                </td>

                <td>
                  <div className="organization-actions">
                    <button
                      type="button"
                      className="organization-view-btn"
                      onClick={() => setSelectedEntity(entity)}
                    >
                      View
                    </button>

                    <button
                      type="button"
                      className="organization-edit-btn"
                      onClick={() => onEdit(entity)}
                    >
                      Edit
                    </button>

                    <button
                      type="button"
                      className="organization-delete-btn"
                      onClick={() => onDelete(entity)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedEntity && (
        <div
          className="organization-modal-overlay"
          onClick={() => setSelectedEntity(null)}
        >
          <div
            className="organization-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="organization-modal-header">
              <div>
                <h3>Legal Entity Details</h3>
                <p>Complete legal entity and tax registration information</p>
              </div>

              <button
                type="button"
                className="organization-modal-close"
                onClick={() => setSelectedEntity(null)}
              >
                ×
              </button>
            </div>

            <div className="organization-modal-body">
              <div className="organization-detail-section">
                <div className="organization-detail-section-title">
                  Legal Entity Information
                </div>

                <div className="organization-detail-grid">
                  <div className="organization-detail-item">
                    <span>Registration Number</span>
                    <strong>{selectedEntity.registration_number || "-"}</strong>
                  </div>

                  <div className="organization-detail-item">
                    <span>Country of Incorporation</span>
                    <strong>{selectedEntity.country_of_incorporation || "-"}</strong>
                  </div>

                  <div className="organization-detail-item">
                    <span>Incorporation Date</span>
                    <strong>{formatDate(selectedEntity.incorporation_date)}</strong>
                  </div>

                  <div className="organization-detail-item">
                    <span>Status</span>
                    <strong>
                      <span className={`organization-status ${getStatusClass(selectedEntity.status)}`}>
                        {selectedEntity.status || "Pending"}
                      </span>
                    </strong>
                  </div>

                  <div className="organization-detail-item">
                    <span>Public ID</span>
                    <strong>{selectedEntity.public_id || "-"}</strong>
                  </div>

                  <div className="organization-detail-item">
                    <span>Tenant ID</span>
                    <strong>{selectedEntity.tenant || "-"}</strong>
                  </div>

                  <div className="organization-detail-item">
                    <span>Created At</span>
                    <strong>{formatDateTime(selectedEntity.created_at)}</strong>
                  </div>

                  <div className="organization-detail-item">
                    <span>Updated At</span>
                    <strong>{formatDateTime(selectedEntity.updated_at)}</strong>
                  </div>
                </div>
              </div>

              <div className="organization-detail-section">
                <div className="organization-detail-section-title">
                  Review Information
                </div>

                <div className="organization-detail-grid">
                  <div className="organization-detail-item">
                    <span>Reviewed At</span>
                    <strong>{formatDateTime(selectedEntity.reviewed_at)}</strong>
                  </div>

                  <div className="organization-detail-item">
                    <span>Reviewed By</span>
                    <strong>{selectedEntity.reviewed_by || "-"}</strong>
                  </div>

                  <div className="organization-detail-item">
                    <span>Requested By</span>
                    <strong>{selectedEntity.requested_by || "-"}</strong>
                  </div>

                  <div className="organization-detail-item">
                    <span>Rejection Reason</span>
                    <strong>{selectedEntity.rejection_reason || "-"}</strong>
                  </div>
                </div>
              </div>

              <div className="organization-detail-section">
                <div
                  className="organization-detail-section-title"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>Tax Registrations</span>

                  <span className="organization-type-pill">
                    {Array.isArray(selectedEntity.tax_registrations)
                      ? selectedEntity.tax_registrations.length
                      : 0}
                  </span>
                </div>

                {!Array.isArray(selectedEntity.tax_registrations) ||
                selectedEntity.tax_registrations.length === 0 ? (
                  <div className="organization-no-data">
                    No tax registrations available.
                  </div>
                ) : (
                  <div className="organization-tax-details-list">
                    {selectedEntity.tax_registrations.map((tax, index) => (
                      <div
                        className="organization-tax-detail-card"
                        key={tax.id || index}
                      >
                        <div className="organization-tax-detail-header">
                          <strong>Tax Registration {index + 1}</strong>

                          <span className={`organization-status ${getStatusClass(tax.status)}`}>
                            {tax.status || "Pending"}
                          </span>
                        </div>

                        <div className="organization-detail-grid">
                          <div className="organization-detail-item">
                            <span>Tax Type</span>
                            <strong>{tax.tax_type || "-"}</strong>
                          </div>

                          <div className="organization-detail-item">
                            <span>Country Code</span>
                            <strong>{tax.country_code || "-"}</strong>
                          </div>

                          <div className="organization-detail-item">
                            <span>Tax Number</span>
                            <strong>{tax.tax_number || "-"}</strong>
                          </div>

                          <div className="organization-detail-item">
                            <span>Tax Registration ID</span>
                            <strong>{tax.id || "-"}</strong>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="organization-modal-footer">
              <button
                type="button"
                className="organization-cancel-btn"
                onClick={() => setSelectedEntity(null)}
              >
                Close
              </button>

              <button
                type="button"
                className="organization-edit-btn"
                onClick={() => {
                  const entity = selectedEntity;
                  setSelectedEntity(null);
                  onEdit(entity);
                }}
              >
                Edit Legal Entity
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LegalEntityTable;
